import { useQuery } from '@tanstack/react-query';
import durationSubOptions from 'data/subOptions/durationSubOptions';
import priceSubOptions from 'data/subOptions/priceSubOptions';
import transportationModeSubOptions from 'data/subOptions/transportationModeSubOptions';
import { fetchSearchWithOther } from 'flux/clients/experiences';
import { Filter } from 'model/Filters';
import { ActiveFilters } from 'modules/common/Popovers/MoreFiltersPopover/hooks/useMoreFilters';
import { useMemo } from 'react';

function useAmountOfSearchResults({
  keywords,
  activitiesFilterStatus,
  toursFilterStatus,
  activitiesOptions,
  topFilterOptions,
  durationOptions,
  toursOptions,
  transportModeOptions,
  priceOptions,
  otherFilters,
  city,
  country,
}: ActiveFilters & {
  otherFilters: Filter[];
  city?: string;
  country?: string;
}) {
  const filterParams = useMemo(() => {
    const params = new URLSearchParams();
    if (keywords.length > 0) {
      params.append('query', keywords.join('+'));
    }
    if (transportModeOptions.length > 0) {
      transportationModeSubOptions.forEach(({ value, queryValue }) => {
        if (transportModeOptions.includes(value)) {
          params.append('other.transport_mode.items[]', queryValue);
        }
      });
    }
    if (durationOptions.length > 0) {
      durationSubOptions.forEach(({ value }) => {
        if (durationOptions.includes(value)) {
          params.append('duration.items[]', value);
        }
      });
    }

    if (priceOptions.length > 0) {
      priceSubOptions.forEach(({ value }) => {
        if (priceOptions.includes(value)) {
          params.append('price.items[]', value);
        }
      });
    }

    if (topFilterOptions.length > 0) {
      if (topFilterOptions.includes('family-friendly')) {
        params.append('other.familyfriendly', 'true');
      }
      if (topFilterOptions.includes('instantconfirmation')) {
        params.append('date.instantconfirmation', 'true');
      }
    }

    if (activitiesFilterStatus && !toursFilterStatus) {
      params.append('activityType.activity', 'activities');
    }

    if (toursFilterStatus && !activitiesFilterStatus) {
      params.append('activityType.activity', 'tours');
    }

    const subcagoryOptions = [...activitiesOptions, ...toursOptions];

    if (
      subcagoryOptions.length > 0 &&
      !toursFilterStatus &&
      !activitiesFilterStatus
    ) {
      subcagoryOptions.forEach((value) => {
        if (value === 'city-tours') {
          params.append('subcategory.items[]', 'city-highlight-tours');
        } else {
          params.append('subcategory.items[]', value);
        }
      });
    }

    if (city) {
      params.append('location.city', city);
    }

    if (country) {
      params.append('location.country', country);
    }

    if (otherFilters.length > 0) {
      otherFilters.forEach((filter) => {
        if (filter.subOptions.length > 0) {
          filter.subOptions.forEach((subOption) => {
            params.append(filter.uid, subOption.value);
          });
        } else {
          if (filter.value) {
            params.append(filter.uid, filter.value);
          } else if (filter.uid === 'date.to') {
            const dateFrom = otherFilters.find(
              (filter) => filter.uid === 'date.from',
            );
            if (dateFrom.value) {
              params.append(filter.uid, dateFrom.value);
            }
          }
        }
      });
    }

    return params;
  }, [
    keywords,
    activitiesFilterStatus,
    toursFilterStatus,
    activitiesOptions,
    topFilterOptions,
    durationOptions,
    toursOptions,
    transportModeOptions,
    priceOptions,
    otherFilters,
    city,
    country,
  ]);

  const { data, isLoading, isFetching } = useQuery(
    ['searchResults', filterParams.toString()],
    () => fetchSearchWithOther(filterParams),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
    },
  );

  return {
    amountOfResults: data?.all?.hits?.total || 0,
    resultsLoading: isLoading || isFetching,
  };
}

export default useAmountOfSearchResults;
